import React, { useState, useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"
import {
  Alert,
  Container,
  Table,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import { useQuery } from "@tanstack/react-query"

import TicketItemList from "components/sales/ticket-item-list"
import storage from "services/storage"
import { authUserKey } from "config/API"
import { Loading } from "components"
import ListHeader from "ui-componets/list-header"
import ListFilterBar from "ui-componets/list-filter-bar"
import { getTickets } from "services/tickets"
import TablePaginationFooter from "components/TablePaginationFooter"
import TicketByUser from "./ticket-by-user"
import TicketRetrive from "./ticketRetrive"
import { getDateFilterParams } from "utils/date-filters"
import "assets/scss/custom/components/table-interactive.scss"

const PAGE_SIZE = 30

const getSingleFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.length === 1 ? value[0] : "all"
  }

  return value
}

const getPaymentStatusFilterParams = (value) => {
  if (value === "pagare") {
    return { is_receivable: "true" }
  }

  if (value === "true" || value === "false") {
    return { is_paid: value, is_receivable: "false" }
  }

  return {}
}

const paymentMethodOptions = [
  { value: "all", label: "Todos" },
  { value: "01 - Efectivo.", label: "Efectivo" },
  { value: "28 - Tarjeta de débito.", label: "Tarjeta" },
  { value: "03 - Transferencia electrónica de fondos.", label: "Transferencia" },
  { value: "PAGARE - Pagare.", label: "Pagare" },
  { value: "99 - Por definir.", label: "Otros" },
]

const getPaymentMethodFilterParams = (value) => {
  const values = Array.isArray(value) ? value : value && value !== "all" ? [value] : []

  return values.length ? { payment_method: values.join(",") } : {}
}

const TicketList = () => {
  document.title = "Lista de Ventas | Punto de venta"

  const Location = useLocation()
  const [filters, setFilters] = useState("")
  const [searchTxt, setSearchTxt] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [searchTerm, setSearchTerm] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [dateFilter, setDateFilter] = useState({ preset: "", startDate: "", endDate: "" })
  const [paymentStatus, setPaymentStatus] = useState("all")
  const [paymentMethod, setPaymentMethod] = useState("all")
  const [saleStatus, setSaleStatus] = useState("active")
  const [currentPage, setCurrentPage] = useState(1)

  const permissions = storage.get(authUserKey).user_permissions || []
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1)
      setSearchTerm(searchTxt.trim())
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTxt])

  const queryFilters = useMemo(
    () => {
      const paymentStatusValue = getSingleFilterValue(paymentStatus)
      const saleStatusValue = getSingleFilterValue(saleStatus)

      return {
        offset: (currentPage - 1) * PAGE_SIZE,
        per_page: PAGE_SIZE,
        ...(searchTerm ? { search: searchTerm } : {}),
        ...getDateFilterParams(dateFilter, "date"),
        ...getPaymentStatusFilterParams(paymentStatusValue),
        ...getPaymentMethodFilterParams(paymentMethod),
        ...(saleStatusValue === "deleted" ? { include_deleted_pagares: "true" } : {}),
        ...(saleStatusValue === "active" ? { deleted_at__isnull: "true" } : {}),
        ordering: "-created_at",
      }
    },
    [currentPage, dateFilter, paymentMethod, paymentStatus, saleStatus, searchTerm]
  )

  const tickets = useQuery(["tickets", queryFilters], () => getTickets(queryFilters), {
    keepPreviousData: true,
  })
  const isLoading = tickets.isLoading
  const [modalTicketOpen, setModalTicketOpen] = useState(false)
  const [ticketId, setTicketId] = useState(null)
  const [state, setState] = useState({ activeTab: "sales" })
  const StyleTableHeaders = {
    position: "sticky",
    background: "white",
    top: "0px",
    zIndex: "3",
    verticalAlign: "middle",
  }

  const StyleTableHeaderMoney = {
    position: "sticky",
    background: "white",
    top: "0px",
    zIndex: "3",
    textAlign: "right",
    verticalAlign: "middle",
  }

  const totalCount = tickets.data?.count || 0
  const totalPages = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1

  const openTicketModal = (id) => {
    setTicketId(id)
    setModalTicketOpen(true)
  }

  const closeTicketModal = () => {
    setModalTicketOpen(false)
  }
  const handleSearch = (pageParams) => {
    const params = new URLSearchParams(filters)
    if (pageParams) {
      const newParams = new URLSearchParams(pageParams)
      window.history.replaceState({}, null, `${Location.pathname}?${newParams}`)
      setFilters(newParams)
      setCurrentPage(Math.floor(Number(newParams.get("offset") || 0) / PAGE_SIZE) + 1)
      return
    }

    setCurrentPage(1)
    const trimmedSearch = searchTxt.trim()
    setSearchTerm(trimmedSearch)
    if (trimmedSearch) {
      params.set("search", trimmedSearch)
    } else {
      params.delete("search")
    }
    params.delete("offset")
    params.delete("per_page")
    window.history.replaceState({}, null, `${Location.pathname}?${params}`)
    setFilters(params)
  }

  function handleChangeSearch(event) {
    setSearchTxt(event.target.value)
  }

  const handleDateFilterChange = (nextDateFilter) => {
    setCurrentPage(1)
    setDateFilter(nextDateFilter)
  }

  const handlePaymentStatusChange = (value) => {
    setCurrentPage(1)
    setPaymentStatus(value)
  }

  const handlePaymentMethodChange = (value) => {
    setCurrentPage(1)
    setPaymentMethod(value)
  }

  const handleSaleStatusChange = (value) => {
    setCurrentPage(1)
    setSaleStatus(value)
  }

  const clearListFilters = () => {
    setCurrentPage(1)
    setDateFilter({ preset: "", startDate: "", endDate: "" })
    setPaymentStatus("all")
    setPaymentMethod("all")
    setSaleStatus("active")
  }

  const handleChangePage = (nextPage) => {
    if (nextPage < 1) return
    if (totalPages > 0 && nextPage > totalPages) return
    setCurrentPage(nextPage)
  }

  const toggleActiveTab = (tab) => {
    if (state.activeTab !== tab) {
      setState({
        activeTab: tab,
      })
    }
  }

  return !permissions.some((e) => e.codename === "view_ticket") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <div className="page-content">
      <Container fluid>
        <Alert color="danger" isOpen={tickets.isError}>
          {tickets.error?.errors?.map((error) => error?.message)}
        </Alert>

        <h2>Lista de Ventas</h2>
        <div className="bg-white p-3">
          <Nav tabs>
            <NavItem>
              <NavLink
                className={`${state.activeTab === "sales" && "active"}`}
                onClick={() => {
                  toggleActiveTab("sales")
                }}
              >
                Ventas
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={`${state.activeTab === "cashierSales" && "active"}`}
                onClick={() => {
                  toggleActiveTab("cashierSales")
                }}
              >
                Ventas por cajero
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={state.activeTab}>
            <TabPane tabId="sales">
              <div style={{ marginTop: "20px", marginBottom: "20px" }}></div>
              <ListHeader
                handleOnChangeSearch={handleChangeSearch}
                handleSearch={handleSearch}
                searchTxt={searchTxt}
                filtersContent={
                  <div className="ticket-list-filters">
                    <ListFilterBar
                      dateFilter={dateFilter}
                      onDateFilterChange={handleDateFilterChange}
                      selects={[
                        {
                          name: "saleStatus",
                          label: "Estado de venta",
                          icon: "fa fa-receipt",
                          value: saleStatus,
                          onChange: handleSaleStatusChange,
                          options: [
                            { value: "all", label: "Todas" },
                            { value: "active", label: "Activas" },
                            { value: "deleted", label: "Eliminadas" },
                          ],
                        },
                        {
                          name: "paymentStatus",
                          label: "Estado de pago",
                          icon: "fa fa-money-bill",
                          value: paymentStatus,
                          onChange: handlePaymentStatusChange,
                          options: [
                            { value: "all", label: "Todas" },
                            { value: "true", label: "Cobradas" },
                            { value: "false", label: "Pendientes" },
                            { value: "pagare", label: "Pagares" },
                          ],
                        },
                        {
                          name: "paymentMethod",
                          label: "Metodo de pago",
                          icon: "fa fa-credit-card",
                          value: paymentMethod,
                          onChange: handlePaymentMethodChange,
                          options: paymentMethodOptions,
                        },
                      ]}
                      onClear={clearListFilters}
                    />
                  </div>
                }
              />
              {!tickets.error && isLoading ? (
                <div className="my-5 text-center">
                  <Loading isLoading={isLoading} size={2} />
                </div>
              ) : tickets.error && !tickets.isLoading ? (
                <div className="my-5 text-center">
                  <h1>Error intenta mas tarde</h1>
                </div>
              ) : (
                <div
                  style={{ height: "auto", overflow: "auto", marginTop: "30px", marginBottom: "20px" }}
                  className="table-responsive"
                >
                  <Table className="table-hover-effect table-interactive">
                    <thead>
                      <tr>
                        <th style={StyleTableHeaders} className="text-cell">Folio</th>
                        <th style={StyleTableHeaders} className="text-cell">Fecha de creación</th>
                        <th style={StyleTableHeaders} className="text-cell">Cajero</th>
                        <th style={StyleTableHeaders} className="text-cell">Cliente</th>
                        <th style={StyleTableHeaderMoney} className="money-cell">Total</th>
                        <th style={StyleTableHeaders} className="payment-method-cell">Método de pago</th>
                        <th style={StyleTableHeaders} className="text-cell">Estado</th>
                        <th style={StyleTableHeaders} className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.data?.results.length > 0 ? (
                        tickets.data?.results.map((item) => {
                          return (
                            <TicketItemList
                              key={item.id}
                              item={item}
                              isOnlySeller={false}
                              isDeletedView={getSingleFilterValue(saleStatus) === "deleted"}
                              openTicketModal={openTicketModal}
                            />
                          )
                        })
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center">
                            No hay datos
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  {modalTicketOpen && (
                    <Modal
                      centered={true}
                      style={{ maxWidth: "500px", width: "95%" }}
                      backdrop={true}
                      isOpen={modalTicketOpen}
                      toggle={closeTicketModal}
                    >
                      <ModalHeader toggle={closeTicketModal}>Detalle de ticket</ModalHeader>
                      <ModalBody style={{ padding: "0px" }}>
                        <TicketRetrive id={ticketId} isModal={true} />
                      </ModalBody>
                    </Modal>
                  )}
                  <TablePaginationFooter
                    totalCount={totalCount}
                    currentPage={currentPage}
                    pageSize={PAGE_SIZE}
                    itemLabel="ventas"
                    isLoading={isLoading}
                    isFetching={tickets.isFetching}
                    onChangePage={handleChangePage}
                  />
                </div>
              )}
            </TabPane>
            <TabPane tabId="cashierSales">
              <TicketByUser />
            </TabPane>
          </TabContent>
        </div>
      </Container>
    </div>
  )
}

export default TicketList
